import { Input } from "@light-technology/components";
import { Controller, useForm } from "react-hook-form";
import { api } from "../../state/api";
import { type Account } from "../../state/api.types";
import { useMutationWithToast } from "../../utilities/useMutationWithToast";
import { FormCard } from "../shared/FormCard";

type NameForm = {
  firstName: string;
  lastName: string;
};

type NameCardProps = {
  account: Account;
};

export const NameCard = ({ account }: NameCardProps) => {
  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty, isValid },
  } = useForm<NameForm>({
    defaultValues: {
      firstName: account.first_name,
      lastName: account.last_name,
    },
    mode: "onChange",
  });

  const updateUserMutation = api.useUpdateUserMutation();
  const [updateUser, updateUserState] = useMutationWithToast(
    updateUserMutation,
    {
      successMessage: "Your details have been updated",
      errorMessage:
        "Your contact details could not be updated at this time. Please try again later.",
    },
  );

  const onSubmit = handleSubmit(({ firstName, lastName }) => {
    updateUser({ first_name: firstName, last_name: lastName }).then(() => {
      reset({ firstName, lastName });
    });
  });

  return (
    <FormCard
      name="Name"
      buttonText="Save Details"
      onButtonClick={onSubmit}
      isLoading={updateUserState.isLoading}
      isDisabled={!isDirty || !isValid}
      data-ph-capture-attribute-action="update-user-name"
    >
      <div className="flex gap-3xl">
        <Controller
          control={control}
          name="firstName"
          rules={{ required: "First name is required" }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              type="text"
              placeholder="First name"
              isRequired
              validationBehavior="aria"
              isInvalid={fieldState.invalid}
              hint={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="lastName"
          rules={{ required: "Last name is required" }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              type="text"
              placeholder="Last name"
              isRequired
              validationBehavior="aria"
              isInvalid={fieldState.invalid}
              hint={fieldState.error?.message}
            />
          )}
        />
      </div>
    </FormCard>
  );
};
